import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ImageOff } from "lucide-react";
import { ConfirmDialog, FilterBar, ListPage, StatusBadge, useToast, type Column } from "@cx/ui";
import { formatBRL } from "@cx/domain";
import type { ListResult, ProductListItem } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { useSession } from "../../session";
import { errorMessage } from "../../errors";
import type { ProductsSearch } from "./search";
import { saveLabelItems } from "../labels/mm";

const STATUS = { active: ["success", "Ativo"], inactive: ["secondary", "Inativo"] } as const;
const KIND = { P: "Produto", S: "Serviço" } as const;

/** Formato na lista: variação (filho), pai com a contagem de variações, kit ou simples. */
function formatLabel(r: ProductListItem): string {
  if (r.parentId) return "Variação";
  if (r.format === "V")
    return r.variationCount === 1 ? "Com variações (1)" : `Com variações (${r.variationCount})`;
  if (r.format === "E") return "Kit";
  return "Simples";
}

function Thumb({ row }: { row: ProductListItem }) {
  if (!row.imageUrl)
    return (
      <span
        className="d-inline-flex align-items-center justify-content-center rounded border text-muted"
        style={{ width: 40, height: 40 }}
        aria-hidden="true"
      >
        <ImageOff size={16} />
      </span>
    );
  // Imagem decorativa: o nome do produto já está na coluna ao lado.
  return (
    <img
      src={row.imageUrl}
      alt=""
      width={40}
      height={40}
      className="rounded border"
      style={{ objectFit: "cover" }}
    />
  );
}

export function ProductsListPage(): ReactElement {
  const search = useSearch({ from: "/cadastros/produtos" });
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const [selected, setSelected] = useState<string[]>([]);
  const [confirm, setConfirm] = useState(false);
  const list = useInfiniteQuery({
    queryKey: ["products", companyId, search],
    initialPageParam: "",
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(search)) if (v) params.set(k, v);
      if (pageParam) params.set("cursor", pageParam);
      return api<ListResult<ProductListItem>>(`/products?${params}`);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const goList = (s: ProductsSearch) => nav({ to: "/cadastros/produtos", search: s });
  const setFilter = (patch: Partial<ProductsSearch>) => goList({ ...search, ...patch });
  const remove = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await api(`/products/${id}`, { method: "DELETE" });
    },
    onSuccess: (_d, ids) => {
      toast("success", ids.length === 1 ? "Produto excluído" : `${ids.length} produtos excluídos`);
      setSelected([]);
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
    // Mesmo com falha no meio do lote, parte foi excluída: lista e Consulta de exclusões precisam refletir (FE16).
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["products", companyId] });
      qc.invalidateQueries({ queryKey: ["deletions", companyId] });
    },
  });
  const exportFile = useMutation({
    mutationFn: (fileFormat: "csv" | "xlsx") => {
      const { q, status, kind, format, includeVariations, hasImage, hasNcm, sort } = search;
      return api<{ jobId: string }>("/products/export", {
        method: "POST",
        body: JSON.stringify({
          q,
          status,
          kind,
          format,
          includeVariations,
          hasImage,
          hasNcm,
          sort,
          fileFormat,
        }),
      });
    },
    onSuccess: () => {
      toast("info", "Exportação iniciada. Acompanhe em Ações assíncronas, no topo da página.");
      qc.invalidateQueries({ queryKey: ["jobs", companyId] });
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const columns: Column<ProductListItem>[] = [
    { key: "img", header: "Imagem", cell: (r) => <Thumb row={r} />, width: "64px" },
    {
      key: "name",
      header: "Nome",
      cell: (r) => (
        <Link to="/cadastros/produtos/$id" params={{ id: r.id }}>
          {r.name}
        </Link>
      ),
    },
    { key: "sku", header: "SKU", cell: (r) => r.sku ?? "—" },
    { key: "kind", header: "Tipo", cell: (r) => KIND[r.kind] },
    { key: "format", header: "Formato", cell: formatLabel },
    { key: "unit", header: "Un", cell: (r) => r.unit },
    { key: "price", header: "Preço", cell: (r) => formatBRL(r.priceCents), align: "end" },
    {
      key: "status",
      header: "Situação",
      cell: (r) => <StatusBadge tone={STATUS[r.status][0]}>{STATUS[r.status][1]}</StatusBadge>,
    },
  ];
  const activeFilters = [
    search.q,
    search.status,
    search.kind,
    search.format,
    search.includeVariations,
    search.hasImage,
    search.hasNcm,
  ].filter(Boolean).length;
  const canCreate = can(perms, "products", "create");
  const select = (label: string, key: keyof ProductsSearch, options: [string, string][], all: string) => (
    <select
      className="form-select w-auto"
      aria-label={label}
      value={search[key] ?? ""}
      onChange={(e) => setFilter({ [key]: e.target.value || undefined })}
    >
      <option value="">{all}</option>
      {options.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  );
  return (
    <>
      <ListPage<ProductListItem>
        title="Produtos"
        breadcrumb={["Cadastros", "Produtos"]}
        primaryAction={
          <>
            {can(perms, "products", "export") && (
              <div className="btn-group" role="group" aria-label="Exportar">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={exportFile.isPending}
                  onClick={() => exportFile.mutate("csv")}
                >
                  Exportar CSV
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={exportFile.isPending}
                  onClick={() => exportFile.mutate("xlsx")}
                >
                  Exportar Excel
                </button>
              </div>
            )}
            {canCreate && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => nav({ to: "/preferencias/importacoes", search: { tipo: "products" } })}
              >
                Importar planilha
              </button>
            )}
            {canCreate && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => nav({ to: "/cadastros/produtos/novo" })}
              >
                Incluir produto
              </button>
            )}
          </>
        }
        filters={
          <FilterBar
            search={search.q ?? ""}
            onSearch={(q) => setFilter({ q: q || undefined })}
            activeCount={activeFilters}
            onClear={() => goList({})}
            placeholder="Nome, SKU, GTIN ou descrição"
          >
            {select(
              "Situação",
              "status",
              [
                ["active", "Ativos"],
                ["inactive", "Inativos"],
              ],
              "Todas as situações",
            )}
            {select(
              "Tipo",
              "kind",
              [
                ["P", "Produto"],
                ["S", "Serviço"],
              ],
              "Produtos e serviços",
            )}
            {select(
              "Formato",
              "format",
              [
                ["S", "Simples"],
                ["V", "Com variações"],
                ["E", "Com composição (kit)"],
              ],
              "Todos os formatos",
            )}
            {select("Variações", "includeVariations", [["yes", "Mostrar variações"]], "Sem variações")}
            {select(
              "Imagem",
              "hasImage",
              [
                ["yes", "Com imagem"],
                ["no", "Sem imagem"],
              ],
              "Com ou sem imagem",
            )}
            {select(
              "NCM",
              "hasNcm",
              [
                ["yes", "Com NCM"],
                ["no", "Sem NCM"],
              ],
              "Com ou sem NCM",
            )}
            {select("Ordem", "sort", [["recent", "Últimos incluídos"]], "Ordem alfabética")}
          </FilterBar>
        }
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        selected={selected}
        onSelectedChange={setSelected}
        selectionScope={JSON.stringify(search)}
        bulkActions={[
          ...(can(perms, "label_templates", "read")
            ? [
                {
                  label: "Imprimir etiquetas",
                  onClick: () => {
                    // T3.6: a seleção vai para a impressão com nome e SKU (até 200 registros).
                    const chosen = new Set(selected);
                    saveLabelItems(
                      rows
                        .filter((r) => chosen.has(r.id))
                        .map((r) => ({
                          id: r.id,
                          name: r.name,
                          detail: r.sku ? `SKU ${r.sku}` : "",
                          copies: 1,
                        })),
                    );
                    nav({ to: "/cadastros/etiquetas/imprimir", search: { fonte: "products" } });
                  },
                },
              ]
            : []),
          ...(can(perms, "products", "delete")
            ? [{ label: "Excluir selecionados", tone: "danger" as const, onClick: () => setConfirm(true) }]
            : []),
        ]}
        // O total vem só da 1ª página: nas páginas com cursor a API devolve summary.count = -1.
        info={{ count: list.data?.pages[0]?.summary.count ?? 0 }}
        empty={{
          title: activeFilters
            ? "Nenhum produto encontrado com esses filtros"
            : "Nenhum produto cadastrado ainda",
          description: activeFilters
            ? undefined
            : "Cadastre manualmente agora. A importação por planilha chega em breve.",
          actions: activeFilters
            ? [{ label: "Limpar filtros", onClick: () => goList({}) }]
            : canCreate
              ? [{ label: "Incluir produto", onClick: () => nav({ to: "/cadastros/produtos/novo" }) }]
              : [],
        }}
        loading={list.isLoading || list.isFetchingNextPage}
        error={list.isError ? list.error.message : undefined}
        hasMore={list.hasNextPage}
        onLoadMore={() => list.fetchNextPage()}
        onRowOpen={(r) => nav({ to: "/cadastros/produtos/$id", params: { id: r.id } })}
      />
      <ConfirmDialog
        open={confirm}
        title="Excluir produtos"
        message={`Excluir ${selected.length} ${selected.length === 1 ? "produto" : "produtos"}? Você pode restaurar em Preferências › Consulta de exclusões.`}
        confirmLabel="Excluir"
        onConfirm={() => {
          setConfirm(false);
          remove.mutate(selected);
        }}
        onCancel={() => setConfirm(false)}
      />
    </>
  );
}
